import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { MuteButton } from '../components/NotificationProvider';
import { JiraDropdown } from '../components/JiraDropdown';

interface Branch {
  id: string;
  name: string;
  location: string;
}

interface ConfigValue {
  id: string;
  label: string;
  value: string;
  enabled?: boolean;
}

export default function CreateRequest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [userBranches, setUserBranches] = useState<Set<string>>(new Set());
  const [reportingTypeOptions, setReportingTypeOptions] = useState<ConfigValue[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [hasCreatePermission, setHasCreatePermission] = useState(true);
  const [formData, setFormData] = useState({
    description: '',
    reportingType: '',
    location: '',
    branchId: '',
  });

  // Check if user has create request permission
  const checkCreatePermission = async () => {
    try {
      const response = await api.get<any>('/roles');
      if (response.roles && Array.isArray(response.roles)) {
        const userRole = response.roles.find((r: any) => r.name === user?.role);
        if (userRole && userRole.permissions) {
          const perms = typeof userRole.permissions === 'string'
            ? JSON.parse(userRole.permissions)
            : userRole.permissions;
          const hasPermission = perms.requests?.create === true;
          setHasCreatePermission(hasPermission);

          // Redirect if no permission
          if (!hasPermission) {
            navigate('/requests');
          }
        }
      }
    } catch (error) {
      console.error('Failed to check permissions:', error);
    }
  };

  // Fetch user's accessible branches
  const fetchUserBranches = async () => {
    try {
      const response = await api.get<{ branches: Array<{ id: string; name: string; location: string; isDefault: boolean }> }>('/branches');
      if (response.branches) {
        const branches = response.branches;
        setBranches(branches);

        // Extract branch IDs user has access to
        const branchIds = new Set(branches.map(b => b.id));
        setUserBranches(branchIds);

        // Pre-select the default branch if available
        const defaultBranch = branches.find(b => b.isDefault);
        if (defaultBranch) {
          setFormData(prev => ({ ...prev, branchId: defaultBranch.id }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      // Fallback to demo branches if API fails
      setBranches([
        { id: 'demo-north-branch', name: 'North District', location: '123 Main St, Downtown' },
        { id: 'demo-south-branch', name: 'South District', location: '456 Oak Ave, Southside' },
      ]);
    }
  };

  // Fetch reporting type configuration
  const fetchReportingTypeConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await api.get<any>('/setup/request-configs/reporting_type');
      if (response.config) {
        const enabledValues: ConfigValue[] = [];

        // Add enabled default values
        if (response.config.useDefaults && response.config.defaultValues) {
          enabledValues.push(...response.config.defaultValues.filter((v: any) => v.enabled !== false));
        }

        // Add enabled custom values
        if (response.config.customValues) {
          enabledValues.push(...response.config.customValues.filter((v: any) => v.enabled));
        }

        setReportingTypeOptions(enabledValues);
      }
    } catch (error) {
      console.error('Failed to fetch reporting type config:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkCreatePermission();
      fetchUserBranches();
      fetchReportingTypeConfig();
    }
  }, [user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.description.trim()) {
        alert('Please enter a description');
        setLoading(false);
        return;
      }
      if (!formData.reportingType.trim()) {
        alert('Please select a reporting type');
        setLoading(false);
        return;
      }
      if (!formData.location.trim()) {
        alert('Please enter a location');
        setLoading(false);
        return;
      }
      if (!formData.branchId) {
        alert('Please select a branch');
        setLoading(false);
        return;
      }

      console.log('[CreateRequest] Submitting form data:', formData);
      await api.post('/requests', formData);
      navigate('/requests');
    } catch (error) {
      console.error('Failed to create request:', error);
      alert('Failed to create request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="p-4 md:p-8">
      <button
        onClick={() => navigate('/requests')}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to requests
      </button>

      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Service Request</h1>
          <p className="text-gray-500 mt-1">Fill out the form to create a new service request</p>
        </div>
        <MuteButton />
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-200 space-y-6">
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              required
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              placeholder="Detailed description of the issue..."
            />
          </div>

          {/* Reporting Type */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="reportingType" className="block text-sm font-medium text-gray-700 mb-2">
                Reporting Type *
              </label>
              <JiraDropdown
                options={reportingTypeOptions}
                value={formData.reportingType}
                onChange={(value) => setFormData(prev => ({ ...prev, reportingType: value }))}
                placeholder="Select reporting type..."
                disabled={loadingConfig}
                loading={loadingConfig}
              />
            </div>
          </div>

          {/* Location & Branch */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                required
                value={formData.location}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Street address or landmark"
              />
            </div>

            <div>
              <label htmlFor="branchId" className="block text-sm font-medium text-gray-700 mb-2">
                Branch
              </label>
              <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50">
                <p className="text-gray-600 font-medium">
                  {branches.find(b => b.id === formData.branchId)?.name || 'Loading...'}
                </p>
                {branches.find(b => b.id === formData.branchId)?.location && (
                  <p className="text-xs text-gray-500 mt-1">
                    {branches.find(b => b.id === formData.branchId)?.location}
                  </p>
                )}
              </div>
              <input
                type="hidden"
                id="branchId"
                name="branchId"
                value={formData.branchId}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/requests')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {loading ? 'Creating...' : 'Create Request'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
